import React from 'react';
import * as THREE from 'three';

interface ClampStandProps {
  visible: boolean;
  position?: [number, number, number];
  rotation?: [number, number, number];
  armHeight?: number;
}

const ClampStand: React.FC<ClampStandProps> = ({ visible, position = [1.15, -1.65, 0], rotation = [0, 0, 0], armHeight = 1.6 }) => {
  return (
    <group position={position} rotation={rotation} scale={visible ? 1 : 0}>
      {/* Base Plane */}
      <mesh>
        <boxGeometry args={[0.75, 0.04, 0.35]} />
        <meshStandardMaterial color="#808090" roughness={0.5} metalness={0.45} />
      </mesh>
      {/* Vertical Rod */}
      <mesh position={[0, 1.4, 0]}>
        <cylinderGeometry args={[0.025, 0.025, 2.8, 12]} />
        <meshStandardMaterial color="#808090" roughness={0.4} metalness={0.55} />
      </mesh>
      
      {/* Moveable Arm & Clamp */}
      <group position={[0, armHeight, 0]}>
        {/* Bosshead (Junction block) */}
        <mesh position={[-0.08, 0, 0]}>
          <boxGeometry args={[0.2, 0.1, 0.1]} />
          <meshStandardMaterial color="#303030" roughness={0.7} metalness={0.1} />
        </mesh>
        {/* Bosshead screw handles */}
        <mesh position={[0, 0, 0.08]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.1, 12]} />
          <meshStandardMaterial color="#707080" roughness={0.4} metalness={0.6} />
        </mesh>
        <mesh position={[-0.15, 0.1, 0]} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.1, 12]} />
          <meshStandardMaterial color="#707080" roughness={0.4} metalness={0.6} />
        </mesh>

        {/* Horizontal Arm */}
        <mesh position={[-0.35, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.02, 0.02, 0.65, 12]} />
          <meshStandardMaterial color="#707080" roughness={0.4} metalness={0.6} />
        </mesh>
        
        {/* Realistic Burette Clamp Head */}
        {/* The center of the clamp grip is exactly at [-0.68, 0, 0] for perfect alignment measuring from the rod */}
        <group position={[-0.68, 0, 0]}>
          {/* Clamp Base Joint (Connects to rod) */}
          <mesh position={[0.07, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.08, 16]} />
            <meshStandardMaterial color="#606070" roughness={0.5} metalness={0.5} />
          </mesh>

          {/* Pivot / Knob body */}
          <mesh position={[0.04, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.025, 0.025, 0.07, 16]} />
            <meshStandardMaterial color="#404040" metalness={0.4} roughness={0.6} />
          </mesh>

          {/* Adjusting Screw extending out */}
          <mesh position={[0.04, 0, 0.05]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.005, 0.005, 0.05, 8]} />
            <meshStandardMaterial color="#707080" />
          </mesh>
          <mesh position={[0.04, 0, 0.08]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.015, 0.015, 0.02, 12]} />
            <meshStandardMaterial color="#111" />
          </mesh>

          {/* Jaw 1 (Front/Top) covering 160 degrees */}
          <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, -Math.PI / 8]}>
            <torusGeometry args={[0.055, 0.015, 12, 24, Math.PI * 0.9]} />
            <meshStandardMaterial color="#151515" roughness={0.9} />
          </mesh>

          {/* Jaw 2 (Back/Bottom) covering 160 degrees */}
          <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, -Math.PI / 8]}>
            <torusGeometry args={[0.055, 0.015, 12, 24, Math.PI * 0.9]} />
            <meshStandardMaterial color="#151515" roughness={0.9} />
          </mesh>
          
          {/* Jaw connecting arms */}
          <mesh position={[0.025, 0, 0.04]} rotation={[0, -Math.PI / 4, 0]}>
            <cylinderGeometry args={[0.01, 0.01, 0.03, 8]} />
            <meshStandardMaterial color="#151515" />
          </mesh>
          <mesh position={[0.025, 0, -0.04]} rotation={[0, Math.PI / 4, 0]}>
            <cylinderGeometry args={[0.01, 0.01, 0.03, 8]} />
            <meshStandardMaterial color="#151515" />
          </mesh>
        </group>
      </group>
    </group>
  );
};

export default ClampStand;